import { useEffect, useMemo, useState } from 'react'
import { SmartBack } from '../navigation/SmartBack.js'
import { ArrowLeftRight, ArrowRight } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { Select } from '../admin/primitives.js'
import { Chart, type ChartSeries } from './Chart.js'
import { jourFrAnnee, tPlus, type SoireeStats } from './lib.js'
import { useMenuSoiree } from './menu-soiree.js'

/**
 * /soirees/compare?a=…&b=… — COMPARER DEUX SOIRÉES.
 *
 * URL partageable. Courbes normalisées sur T+écoulé depuis le début de
 * CHAQUE soirée (jamais 23:00 vs 23:00 si les débuts diffèrent), préventes
 * aux jalons J-30 → H-6, différences absolues + %. Réutilise le moteur
 * Analytics existant — aucun second moteur de graphiques.
 */

interface PairSide {
  id: string; name: string; starts_at: string; family: string | null
  stats: SoireeStats | null
  presales: Record<string, number | null>
  revenue_cents: number | null
}
interface PairData {
  a: PairSide; b: PairSide
  diffs: { key: string; label: string; a: number | null; b: number | null; delta: number | null; delta_pct: number | null }[]
}
interface Suggestion { mode: string; id: string; name: string; starts_at: string }
interface Candidate { id: string; name: string; starts_at: string; family: string | null }

const MILESTONES = ['J-30', 'J-21', 'J-14', 'J-7', 'J-3', 'J-1', 'H-12', 'H-6', 'Début']
const MILESTONE_KEYS = ['J-30', 'J-21', 'J-14', 'J-7', 'J-3', 'J-1', 'H-12', 'H-6', 'START']

function euros(cents: number | null): string {
  return cents === null ? '—' : `${(cents / 100).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try { const r = await fetch(url, { credentials: 'same-origin' }); return r.ok ? (await r.json()) as T : null }
  catch { return null }
}

function EventPicker({ label, value, candidates, onChange }: {
  label: string
  value: string | null
  candidates: Candidate[]
  onChange: (id: string) => void
}) {
  const options = candidates.map((c) => ({
    value: c.id,
    label: `${c.name} — ${new Date(c.starts_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris' })}`,
  }))
  return (
    <div className="cmp-picker">
      <span className="cmp-picker__label">{label}</span>
      <Select value={value ?? ''} options={options} onChange={onChange}
        placeholder="Choisir une soirée" ariaLabel={label} />
    </div>
  )
}

export function ComparePage({ a: initialA, b: initialB }: { a: string | null; b: string | null }) {
  const meta = useShellMeta()
  const menuSoiree = useMenuSoiree()
  // a/b vivent en état local (le routeur ne notifie pas les changements de
  // query) ; l'URL est maintenue en miroir (replaceState) pour rester
  // partageable.
  const [a, setAState] = useState(initialA)
  const [b, setBState] = useState(initialB)
  useEffect(() => {
    const qs = new URLSearchParams()
    if (a) qs.set('a', a)
    if (b) qs.set('b', b)
    window.history.replaceState(null, '', `/soirees/compare${qs.size > 0 ? `?${qs}` : ''}`)
  }, [a, b])
  const [pair, setPair] = useState<PairData | null>(null)
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [curves, setCurves] = useState<{ a: ChartSeries | null; b: ChartSeries | null; bucketS: number }>({ a: null, b: null, bucketS: 1800 })
  const [loading, setLoading] = useState(false)

  // Candidats + suggestions (basées sur A quand A est choisi).
  useEffect(() => {
    let vivant = true
    const base = a ?? b
    if (base) {
      void fetchJson<{ suggestions: Suggestion[]; all: Candidate[] }>(`/api/v1/soirees/${base}/suggestions`)
        .then((j) => { if (vivant && j) { setSuggestions(j.suggestions); setCandidates([...j.all]) } })
    } else {
      void fetchJson<{ history: Candidate[]; upcoming: Candidate[] }>(`/api/v1/soirees?period=all`)
        .then((j) => { if (vivant && j) setCandidates([...(j.history ?? []), ...(j.upcoming ?? [])]) })
    }
    return () => { vivant = false }
  }, [a, b])

  // Paire + courbes T+écoulé.
  useEffect(() => {
    let vivant = true
    if (!a || !b) { setPair(null); return }
    setLoading(true)
    void fetchJson<PairData>(`/api/v1/soirees/compare-pair?a=${a}&b=${b}`)
      .then((j) => { if (vivant) { setPair(j); setLoading(false) } })
    /* `scope=soiree` : la soirée SEULE, comme le tableau d'indicateurs juste
       au-dessus (comparePair). Sans lui, le serveur prend la portée des
       Réglages — toute la nuit — et la courbe de L'ÉTUDIANTE montait à 934
       quand son tableau disait 710 (revue du 19/10/2026). */
    const curve = (id: string) =>
      fetchJson<{ available: boolean; bucket_s: number; series: { points: { t: string; value: number }[] }[] }>(
        `/api/v1/soirees/${id}/analytics?metric=scans&group_by=none&bucket_s=1800&scope=soiree`)
    void Promise.all([curve(a), curve(b)]).then(([ca, cb]) => {
      if (!vivant) return
      setCurves({
        a: ca?.available ? { key: 'a', label: 'Soirée A', points: ca.series[0]?.points ?? [] } : null,
        b: cb?.available ? { key: 'b', label: 'Soirée B', points: cb.series[0]?.points ?? [] } : null,
        bucketS: ca?.bucket_s ?? 1800,
      })
    })
    return () => { vivant = false }
  }, [a, b])

  // Normalisation T+écoulé : la série B est translatée sur l'axe de A
  // (décalage = différence des instants de début).
  const alignedSeries: ChartSeries[] = useMemo(() => {
    if (!pair) return []
    const out: ChartSeries[] = []
    if (curves.a) out.push({ ...curves.a, label: pair.a.name })
    if (curves.b) {
      const shift = new Date(pair.a.starts_at).getTime() - new Date(pair.b.starts_at).getTime()
      out.push({
        key: 'b', label: pair.b.name,
        points: curves.b.points.map((p) => ({ t: new Date(new Date(p.t).getTime() + shift).toISOString(), value: p.value })),
      })
    }
    return out
  }, [pair, curves])

  const setA = setAState
  const setB = setBState

  return (
    <Shell active="soirees" meta={meta}>
      <main className="lc-page lc-stagger hub--soirees so-page">
        <SmartBack fallback="/soirees" />
        <h1 className="lc-title"><ArrowLeftRight size={26} aria-hidden="true" /> Comparer deux soirées</h1>
        <p className="lc-subtitle">Totaux, préventes aux jalons J-x et courbes alignées sur T+écoulé.</p>

        <div className="cmp-pickers">
          <EventPicker label="Soirée A" value={a} candidates={candidates} onChange={setA} />
          <EventPicker label="Soirée B" value={b} candidates={candidates} onChange={setB} />
        </div>

        {suggestions.length > 0 && (!a || !b) ? (
          <div className="cmp-suggestions">
            <span className="lc-hint">Suggestions :</span>
            {suggestions.map((s2) => (
              <button key={`${s2.mode}-${s2.id}`} type="button" className="cmp-suggestion"
                onClick={() => (a ? setB(s2.id) : setA(s2.id))}>
                {s2.name} · {new Date(s2.starts_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' })}
              </button>
            ))}
          </div>
        ) : null}

        {!a || !b ? (
          <section className="lc-panel gd-panel"><div className="lc-panel__body">
            <p className="lc-hint">Choisissez deux soirées à comparer — l'URL devient partageable.</p>
          </div></section>
        ) : loading && !pair ? (
          <section className="lc-panel gd-panel"><div className="lc-panel__body cmp-skeleton" aria-busy="true">
            <div className="cmp-skeleton__bar" /><div className="cmp-skeleton__bar" /><div className="cmp-skeleton__bar" />
          </div></section>
        ) : pair ? (
          <>
            {/* ═══ têtes ═══ */}
            <div className="cmp-heads">
              {[pair.a, pair.b].map((s2, i) => (
                <Link key={s2.id} href={`/soirees/${s2.id}`} className="cmp-head lc-card lc-card--violet" {...menuSoiree.soiree(s2)}>
                  <span className="lc-card__edge" aria-hidden="true" />
                  <span className="cmp-head__which">Soirée {i === 0 ? 'A' : 'B'}</span>
                  <span className="cmp-head__name">{s2.name}</span>
                  <span className="cmp-head__date">{jourFrAnnee(s2.starts_at)}</span>
                  {s2.family ? <span className="so-head__family">{s2.family}</span> : null}
                  <span className="so-hero__cta">Ouvrir <ArrowRight size={14} aria-hidden="true" /></span>
                </Link>
              ))}
            </div>

            {/* ═══ indicateurs côte à côte ═══ */}
            <section className="lc-panel gd-panel" aria-label="Indicateurs">
              <div className="lc-panel__body lc-panel__body--flush">
                <table className="cmp-table">
                  <thead><tr><th>Indicateur</th><th>A</th><th>B</th><th>Écart</th></tr></thead>
                  <tbody>
                    {pair.diffs.map((d) => (
                      <tr key={d.key}>
                        {/* Les prix affichés des billets : une valeur faciale, jamais un
                            chiffre d'affaires. Sans `finance.live.read`, le serveur retire la ligne. */}
                        <td>{d.key === 'revenue' ? 'Valeur faciale des billets' : d.label}</td>
                        <td>{d.key === 'revenue' ? euros(d.a) : d.a ?? '—'}</td>
                        <td>{d.key === 'revenue' ? euros(d.b) : d.b ?? '—'}</td>
                        <td>
                          {d.delta === null ? '—' : (
                            <span className={`so-delta ${d.delta >= 0 ? 'so-delta--up' : 'so-delta--down'}`}>
                              {d.delta >= 0 ? '+' : ''}{d.key === 'revenue' ? euros(d.delta) : d.delta}
                              {d.delta_pct !== null ? ` (${d.delta_pct >= 0 ? '+' : ''}${d.delta_pct.toLocaleString('fr-FR')} %)` : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ═══ préventes aux jalons ═══ */}
            <section className="lc-panel gd-panel" aria-label="Préventes avant événement">
              <header className="lc-panel__head"><h2 className="lc-section-title">Préventes avant l'événement</h2></header>
              <div className="lc-panel__body lc-panel__body--flush">
                <table className="cmp-table">
                  <thead><tr><th>Jalon</th>{MILESTONES.map((m) => <th key={m}>{m}</th>)}</tr></thead>
                  <tbody>
                    {[pair.a, pair.b].map((s2, i) => (
                      <tr key={s2.id}>
                        <td>{i === 0 ? 'A' : 'B'}</td>
                        {MILESTONE_KEYS.map((k) => <td key={k}>{s2.presales[k] ?? '—'}</td>)}
                      </tr>
                    ))}
                    <tr className="cmp-table__delta">
                      <td>Écart</td>
                      {MILESTONE_KEYS.map((k) => {
                        const va = pair.a.presales[k], vb = pair.b.presales[k]
                        if (va == null || vb == null || vb === 0) return <td key={k}>—</td>
                        const pct = Math.round(((va - vb) / vb) * 1000) / 10
                        return (
                          <td key={k}>
                            <span className={`so-delta ${pct >= 0 ? 'so-delta--up' : 'so-delta--down'}`}>
                              {pct >= 0 ? '+' : ''}{pct.toLocaleString('fr-FR')} %
                            </span>
                          </td>
                        )
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* ═══ courbes T+écoulé ═══ */}
            <section className="lc-panel gd-panel" aria-label="Scans à temps écoulé">
              <header className="lc-panel__head">
                <h2 className="lc-section-title">Scans cumulés — alignés sur T+écoulé</h2>
                <span className="lc-hint">{alignedSeries.length === 2 ? `T+0 = début de chaque soirée` : ''}</span>
              </header>
              <div className="lc-panel__body">
                {alignedSeries.length > 0 ? (
                  <>
                    <Chart kind="cumulative" series={alignedSeries} bucketS={curves.bucketS}
                      ariaLabel="Comparaison des scans cumulés" />
                    <p className="lc-hint">
                      Axe temporel : heures de la soirée A ; la soirée B est translatée pour aligner les débuts
                      ({tPlus(pair.a.starts_at, new Date(pair.a.starts_at).getTime())} commun).
                    </p>
                  </>
                ) : <p className="lc-hint">Aucun horodatage de scan disponible pour ces soirées.</p>}
              </div>
            </section>
          </>
        ) : (
          <p className="gd-calm">Comparaison indisponible.</p>
        )}
      </main>
    </Shell>
  )
}
